import React, { useState } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { User, Mail, Lock, Check } from 'react-feather';
import { signup } from '../services/api';
import { setAuthData } from '../services/auth';

interface SignupPageProps {
  setIsAuthenticated: React.Dispatch<React.SetStateAction<boolean>>;
}

const SignupPage: React.FC<SignupPageProps> = ({ setIsAuthenticated }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [userData, setUserData] = useState({
    name: '',
    username: '',
    email: '',
    password: '',
    referralcode: '',  // Add referralcode here
    confirm_password: ''
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setUserData(prev => ({ ...prev, [name]: value }));

    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!userData.name.trim()) {
      newErrors.name = t('signup.errors.nameRequired');
    }

    if (!userData.username.trim()) {
      newErrors.username = t('signup.errors.usernameRequired');
    }

    if (!userData.email.trim()) {
      newErrors.email = t('signup.errors.emailRequired');
    } else if (!/\S+@\S+\.\S+/.test(userData.email)) {
      newErrors.email = t('signup.errors.emailInvalid');
    }

    if (!userData.password) {
      newErrors.password = t('signup.errors.passwordRequired');
    } else if (userData.password.length < 8) {
      newErrors.password = t('signup.errors.passwordLength');
    }

    if (!userData.confirm_password) {
      newErrors.confirm_password = t('signup.errors.confirmPasswordRequired');
    } else if (userData.password !== userData.confirm_password) {
      newErrors.confirm_password = t('signup.errors.passwordsMustMatch');
    }

    if (!termsAccepted) {
      newErrors.terms = t('signup.errors.termsRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      setError('');

      // Remove confirm_password before sending to API
      const { confirm_password, ...signupData } = userData;

      await signup(signupData);

      // Store authentication data
      // setAuthData(
      //     response.data.access_token,
      //     response.data.refresh_token,
      //     response.data.user
      // );

      // Update authentication state
      setIsAuthenticated(false);

      // Redirect to dashboard
      navigate('/login', { replace: true });

    } catch (error: any) {
      if (error.response?.status === 409) {
        if (error.response.data.error.includes('Email')) {
          setErrors(prev => ({ ...prev, email: t('signup.errors.emailTaken') }));
        } else if (error.response.data.error.includes('Username')) {
          setErrors(prev => ({ ...prev, username: t('signup.errors.usernameTaken') }));
        } else {
          setError(error.response.data.error);
        }
      } else {
        setError(error.response?.data?.error || t('signup.errors.generalError'));
      }
      console.error('Signup error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col md={8} lg={6} xl={5}>
            <div className="text-center mb-4">
              <img
                  src="/logo.png"
                  alt="MyWeeshy Logo"
                  className="me-2"
                  width={50}
                  style={{ objectFit: "cover" }}
              />
              <h5 className="mb-3 d-inline align-middle">
                MyWeeshy
              </h5>
              <p className="text-muted">{t('signup.subtitle')}</p>
            </div>

            <Card className="shadow-sm">
              <Card.Body className="p-4">
                {error && (
                    <Alert variant="danger" onClose={() => setError('')} dismissible>
                      {error}
                    </Alert>
                )}

                <Form onSubmit={handleSubmit}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t('signup.name')}</Form.Label>
                    <div className="input-group">
                    <span className="input-group-text">
                      <User size={18} />
                    </span>
                      <Form.Control
                          type="text"
                          name="name"
                          value={userData.name}
                          onChange={handleChange}
                          placeholder={t('signup.namePlaceholder') as string}
                          isInvalid={!!errors.name}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.name}
                      </Form.Control.Feedback>
                    </div>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>{t('signup.username')}</Form.Label>
                    <div className="input-group">
                      <span className="input-group-text">@</span>
                      <Form.Control
                          type="text"
                          name="username"
                          value={userData.username}
                          onChange={handleChange}
                          placeholder={t('signup.usernamePlaceholder') as string}
                          isInvalid={!!errors.username}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.username}
                      </Form.Control.Feedback>
                    </div>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>{t('signup.email')}</Form.Label>
                    <div className="input-group">
                    <span className="input-group-text">
                      <Mail size={18} />
                    </span>
                      <Form.Control
                          type="email"
                          name="email"
                          value={userData.email}
                          onChange={handleChange}
                          placeholder={t('signup.emailPlaceholder') as string}
                          isInvalid={!!errors.email}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.email}
                      </Form.Control.Feedback>
                    </div>
                  </Form.Group>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>{t('signup.password')}</Form.Label>
                        <div className="input-group">
                        <span className="input-group-text">
                          <Lock size={18} />
                        </span>
                          <Form.Control
                              type="password"
                              name="password"
                              value={userData.password}
                              onChange={handleChange}
                              placeholder={t('signup.passwordPlaceholder') as string}
                              isInvalid={!!errors.password}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.password}
                          </Form.Control.Feedback>
                        </div>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>{t('signup.confirmPassword')}</Form.Label>
                        <Form.Control
                            type="password"
                            name="confirm_password"
                            value={userData.confirm_password}
                            onChange={handleChange}
                            placeholder={t('signup.confirmPasswordPlaceholder') as string}
                            isInvalid={!!errors.confirm_password}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.confirm_password}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                  </Row>

                  {/* Referral Code */}
                  <Form.Group className="mb-3">
                    <Form.Label>{t('signup.referralCode')}</Form.Label>
                    <div className="input-group">
                    <span className="input-group-text">
                      <Mail size={18} />
                    </span>
                      <Form.Control
                          type="text"
                          name="referralcode"
                          value={userData.referralcode} // Bind to referral code
                          onChange={handleChange}
                          placeholder={t('signup.referralCodePlaceholder').toString()}
                      />
                    </div>
                  </Form.Group>

                  <Form.Group className="mb-4">
                    <Form.Check
                        type="checkbox"
                        id="terms-checkbox"
                        isInvalid={!!errors.terms}
                        checked={termsAccepted}
                        onChange={() => setTermsAccepted(!termsAccepted)}
                        label={
                          <span>
                        {t('signup.termsAgree')}{' '}
                            <Link to="/terms" className="text-decoration-none" style={{ color: '#795EFF' }}>
                          {t('signup.termsLink')}
                        </Link>
                            {' '}{t('signup.and')}{' '}
                            <Link to="/privacy" className="text-decoration-none" style={{ color: '#795EFF' }}>
                          {t('signup.privacyLink')}
                        </Link>
                      </span>
                        }
                    />
                    {errors.terms && (
                        <div className="text-danger small mt-1">{errors.terms}</div>
                    )}
                  </Form.Group>

                  <div className="d-grid mb-3">
                    <Button
                        type="submit"
                        disabled={loading}
                        style={{
                          backgroundColor: '#795EFF',
                          borderColor: '#795EFF',
                          color: 'white'
                        }}
                    >
                      {loading ? (
                          <>
                            <Spinner
                                as="span"
                                animation="border"
                                size="sm"
                                role="status"
                                aria-hidden="true"
                                className="me-2"
                            />
                            {t('loading')}
                          </>
                      ) : t('signup.signupButton')}
                    </Button>
                  </div>

                  <div className="text-center">
                    <p className="mb-0">
                      {t('signup.haveAccount')}{' '}
                      <Link
                          to="/login"
                          className="text-decoration-none"
                          style={{ color: '#795EFF' }}
                      >
                        {t('signup.loginLink')}
                      </Link>
                    </p>
                  </div>

                </Form>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
  );
};

export default SignupPage;
